import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Icon from '../../../../../shared/_TBComponents/Icon';
import Modal from '../../../../../shared/_TBComponents/Modal';
import Text from '../../../../../shared/_TBComponents/Text';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const SummaryBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #FFF3F3;
  border: 1px solid #FFBDBD;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const IssueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  max-height: min(400px, 50vh);
  margin-bottom: 20px;
`;

const IssueRow = styled.div`
  padding: 10px 12px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const BulkIssuesModal = ({
  isOpen, onClose, title, issues, entities, successCount, totalCount,
}) => {
  const entityMap = new Map(entities.map((e) => [e.id, e]));

  const resolveLabel = (id) => {
    const user = entityMap.get(id);
    if (!user) return `#${id}`;
    const fullName = `${user.first || ''} ${user.last || ''}`.trim();
    return fullName || user.name || `#${id}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      customBorderRadius='8px'
      customPadding='24px'
      customMaxWidth='520px'
      customMinHeight='200px'
    >
      <ContentWrapper>
        <Text styledTextProps={{
          fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: '#1A1A1A',
        }}
        >
          {title}
        </Text>

        <SummaryBanner>
          <Icon icon='warning' size={18} color='#CC0404' />
          <Text styledTextProps={{ fontSize: '14px', color: '#CC0404' }}>
            {`${successCount} of ${totalCount} updated successfully. ${issues.length} could not be updated:`}
          </Text>
        </SummaryBanner>

        <IssueList>
          {issues.map((issue) => (
            <IssueRow key={issue.userId}>
              <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, marginBottom: '4px' }}>
                {resolveLabel(issue.userId)}
              </Text>
              {(issue.issues || []).map((message, idx) => (
                <Text
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  styledTextProps={{ fontSize: '13px', color: '#757575' }}
                  styledWrapperProps={{ display: 'block' }}
                >
                  {message}
                </Text>
              ))}
            </IssueRow>
          ))}
        </IssueList>

        <Footer>
          <Button
            name='Close'
            fontSize='14px'
            txtColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            customBorderRadius='6px'
            styledTextProps={{ fontWeight: 600 }}
            onClick={onClose}
          />
        </Footer>
      </ContentWrapper>
    </Modal>
  );
};

BulkIssuesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  issues: PropTypes.arrayOf(PropTypes.shape({
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    issues: PropTypes.arrayOf(PropTypes.string),
  })),
  entities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first: PropTypes.string,
    last: PropTypes.string,
    name: PropTypes.string,
  })),
  successCount: PropTypes.number,
  totalCount: PropTypes.number,
};

BulkIssuesModal.defaultProps = {
  issues: [],
  entities: [],
  successCount: 0,
  totalCount: 0,
};

export default BulkIssuesModal;
